import { initPublicHeader } from './header';

import Swiper from 'swiper';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

// AOS
function initAOS() {
    if (!document.querySelector('[data-aos]')) {
        return;
    }

    const loadAOS = async () => {
        await import('aos/dist/aos.css');

        const AOS = (await import('aos')).default;

        AOS.init();

        window.addEventListener('load', function () {
            AOS.refresh();
        });
    };

    if ('requestIdleCallback' in window) {
        window.requestIdleCallback(loadAOS, {
            timeout: 1500,
        });
    } else {
        window.setTimeout(loadAOS, 1000);
    }
}

function initHomeVideo() {
    const localVideoPlayer = document.getElementById('local-video-player');

    if (!localVideoPlayer) {
        return;
    }

    let playerLoaded = false;

    const loadPlayer = async () => {
        if (playerLoaded) {
            return;
        }

        playerLoaded = true;

        await import('plyr/dist/plyr.css');

        const Plyr = (await import('plyr')).default;

        new Plyr(localVideoPlayer, {
            autoplay: false,
            muted: false,
            controls: [
                'play-large',
                'play',
                'progress',
                'current-time',
                'mute',
                'volume',
                'fullscreen',
            ],
            settings: [
                'quality',
                'speed',
            ],
            loop: {
                active: false,
            },
            ratio: '16:9',
            disableContextMenu: true,
            hideControls: true,
            clickToPlay: true,
        });
    };

    if (!('IntersectionObserver' in window)) {
        loadPlayer();
        return;
    }

    const observer = new IntersectionObserver(
        entries => {
            const entry = entries[0];

            if (!entry.isIntersecting) {
                return;
            }

            observer.disconnect();
            loadPlayer();
        },
        {
            root: null,
            rootMargin: '300px 0px',
            threshold: 0.01,
        }
    );

    observer.observe(localVideoPlayer);
}

function initBannerSwiper() {
    if (!document.querySelector('.banner-swiper')) {
        return;
    }

    let progressBar;

    new Swiper('.banner-swiper', {
        modules: [Navigation, Pagination, Autoplay],
        loop: true,
        autoplay: {
            delay: 5000,
            disableOnInteraction: false,
        },
        speed: 1000,
        navigation: {
            nextEl: '.custom-next',
            prevEl: '.custom-prev',
        },
        preventClicks: false,
        preventClicksPropagation: false,
        on: {
            init: function () {
                progressBar = document.querySelector('.scroll-progress');
            },
            autoplayTimeLeft: function (swiper, time, progress) {
                if (progressBar) {
                    progressBar.style.width = ((1 - progress) * 100) + '%';
                }
            },
        },
    });
}

function initOutletsNavigation() {
    const outletsContainer = document.querySelector('.outlets-scroll-container');
    const prevBtn = document.querySelector('.outlets-nav-btn.prev');
    const nextBtn = document.querySelector('.outlets-nav-btn.next');

    if (!outletsContainer || !prevBtn || !nextBtn) {
        return;
    }

    const outletCard = outletsContainer.querySelector('.outlet-card');

    if (!outletCard) {
        prevBtn.hidden = true;
        nextBtn.hidden = true;
        return;
    }

    const cardWidth = outletCard.offsetWidth + 20;

    if (outletsContainer.offsetWidth === outletsContainer.scrollWidth) {
        prevBtn.hidden = true;
        nextBtn.hidden = true;
    }

    function updateButtonStates() {
        const scrollLeft = outletsContainer.scrollLeft;
        const maxScroll = outletsContainer.scrollWidth - outletsContainer.clientWidth;
        prevBtn.disabled = scrollLeft <= 0;
        nextBtn.disabled = scrollLeft >= maxScroll - 1;
    }

    prevBtn.addEventListener('click', function () {
        outletsContainer.scrollBy({
            left: -cardWidth,
            behavior: 'smooth',
        });
    });

    nextBtn.addEventListener('click', function () {
        outletsContainer.scrollBy({
            left: cardWidth,
            behavior: 'smooth',
        });
    });

    outletsContainer.addEventListener('scroll', updateButtonStates);
    updateButtonStates();
}

function initServicesSwiper() {
    if (!document.querySelector('.services-swiper')) {
        return;
    }

    let servicesSwiper;
    let isHovering = false;
    let currentActiveIndex = 0;
    let cardEventListeners = [];

    function updateNavigationButtons() {
        if (!servicesSwiper) {
            return;
        }

        const prevBtn = document.querySelector('.services-swiper-btn.prev');
        const nextBtn = document.querySelector('.services-swiper-btn.next');

        if (prevBtn && nextBtn) {
            prevBtn.disabled = servicesSwiper.isBeginning;
            nextBtn.disabled = servicesSwiper.isEnd;
        }
    }

    function setActiveSlide(slideIndex) {
        if (window.innerWidth < 992 && servicesSwiper) {
            servicesSwiper.slides.forEach(slide => {
                slide.classList.remove('swiper-slide-visible');
            });

            if (servicesSwiper.slides[slideIndex]) {
                servicesSwiper.slides[slideIndex].classList.add('swiper-slide-visible');
                currentActiveIndex = slideIndex;
            }
        }
    }

    function clearAllActiveSlides() {
        if (window.innerWidth < 992 && servicesSwiper) {
            servicesSwiper.slides.forEach(slide => {
                slide.classList.remove('swiper-slide-visible');
            });
        }
    }

    function handleCardHover(card, index) {
        if (window.innerWidth < 992 && servicesSwiper.slides[index]) {
            isHovering = true;
            clearAllActiveSlides();
            servicesSwiper.slides[index].classList.add('swiper-slide-visible');
        }
    }

    function handleCardLeave() {
        if (window.innerWidth < 992) {
            isHovering = false;
            setTimeout(() => {
                if (!isHovering) {
                    clearAllActiveSlides();
                    if (servicesSwiper.slides[currentActiveIndex]) {
                        servicesSwiper.slides[currentActiveIndex].classList.add('swiper-slide-visible');
                    }
                }
            }, 100);
        }
    }

    function removeCardEventListeners() {
        cardEventListeners.forEach(listener => {
            if (listener.element && listener.handler) {
                listener.element.removeEventListener(listener.type, listener.handler);
            }
        });
        cardEventListeners = [];
    }

    function initSwiper() {
        servicesSwiper = new Swiper('.services-swiper', {
            modules: [Navigation, Pagination],
            slidesPerView: 'auto',
            spaceBetween: 5,
            centeredSlides: true,
            centeredSlidesBounds: true,
            loop: false,
            touchRatio: 1,
            grabCursor: true,
            navigation: {
                nextEl: '.services-swiper-btn.next',
                prevEl: '.services-swiper-btn.prev',
            },
            pagination: {
                el: '.services-swiper-progressbar',
                type: 'progressbar',
            },
            breakpoints: {
                992: {
                    slidesPerView: 5,
                    centeredSlides: false,
                    centeredSlidesBounds: false,
                    spaceBetween: 0,
                    allowTouchMove: false,
                    navigation: false,
                    pagination: false,
                    simulateTouch: false,
                    freeMode: false,
                },
            },
            on: {
                init: function () {
                    updateNavigationButtons();
                    if (window.innerWidth < 992) {
                        setActiveSlide(this.realIndex);
                    }
                },
                slideChange: function () {
                    updateNavigationButtons();
                    if (window.innerWidth < 992) {
                        setActiveSlide(this.realIndex);
                    }
                },
            },
        });

        if (window.innerWidth < 992) {
            const cards = document.querySelectorAll('.service-card-swiper');

            cards.forEach((card, index) => {
                const clickHandler = function () {
                    if (servicesSwiper && !servicesSwiper.destroyed) {
                        servicesSwiper.slideTo(index);
                        setActiveSlide(index);
                    }
                };

                const mouseEnterHandler = function () {
                    handleCardHover(this, index);
                };

                const mouseLeaveHandler = function () {
                    handleCardLeave();
                };

                const touchStartHandler = function () {
                    handleCardHover(this, index);
                };

                const touchEndHandler = function () {
                    handleCardLeave();
                };

                card.addEventListener('click', clickHandler);
                card.addEventListener('mouseenter', mouseEnterHandler);
                card.addEventListener('mouseleave', mouseLeaveHandler);
                card.addEventListener('touchstart', touchStartHandler);
                card.addEventListener('touchend', touchEndHandler);

                cardEventListeners.push(
                    { element: card, type: 'click', handler: clickHandler },
                    { element: card, type: 'mouseenter', handler: mouseEnterHandler },
                    { element: card, type: 'mouseleave', handler: mouseLeaveHandler },
                    { element: card, type: 'touchstart', handler: touchStartHandler },
                    { element: card, type: 'touchend', handler: touchEndHandler }
                );
            });

            const swiperContainer = document.querySelector('.services-swiper');
            if (swiperContainer) {
                const containerLeaveHandler = function () {
                    if (!isHovering && servicesSwiper && !servicesSwiper.destroyed) {
                        clearAllActiveSlides();
                        if (servicesSwiper.slides[currentActiveIndex]) {
                            servicesSwiper.slides[currentActiveIndex].classList.add('swiper-slide-visible');
                        }
                    }
                };

                swiperContainer.addEventListener('mouseleave', containerLeaveHandler);
                cardEventListeners.push({ element: swiperContainer, type: 'mouseleave', handler: containerLeaveHandler });
            }
        }
    }

    function destroySwiper() {
        removeCardEventListeners();

        if (servicesSwiper && !servicesSwiper.destroyed) {
            servicesSwiper.destroy(true, true);
            servicesSwiper = null;
        }
    }

    if (window.innerWidth < 992) {
        initSwiper();
    }

    window.addEventListener('resize', function () {
        if (window.innerWidth >= 992) {
            destroySwiper();
            document.querySelectorAll('.services-swiper .swiper-slide').forEach(slide => {
                slide.classList.remove('swiper-slide-visible');
            });
            isHovering = false;
        } else if (!servicesSwiper) {
            initSwiper();
        }
    });

    document.addEventListener('click', function (e) {
        if (e.target.closest('.services-swiper-btn')) {
            setTimeout(updateNavigationButtons, 100);
        }
    });
}

document.addEventListener('DOMContentLoaded', function () {
    initPublicHeader();
    initBannerSwiper();
    initOutletsNavigation();
    initServicesSwiper();
    initHomeVideo();
    initAOS();
});
